import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed
  const [txn, setTxn] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId || !token) return;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          setTxn(res.data);
          await refreshUser();
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('failed');
          return;
        }
        // Keep polling
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    poll();
  }, [sessionId, token, attempts, refreshUser]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: '480px', width: '100%', padding: '40px 24px', textAlign: 'center' }}>

        {status === 'polling' && (
          <div data-testid="payment-polling">
            <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#1E491A', margin: '0 auto 20px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#1E491A', marginBottom: '12px' }}>
              Processing your payment...
            </h1>
            <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
              This should only take a moment. Please don't close this page.
            </p>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="payment-success">
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <CheckCircle2 style={{ width: 36, height: 36, color: '#16a34a' }} />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px', marginBottom: '12px' }}>
              Payment Confirmed
            </h1>
            <p style={{ fontSize: '15px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px' }}>
              Welcome to <strong>{txn?.package_name || 'the SPRING programme'}</strong>. Your stages are unlocked and ready.
            </p>

            <div style={{
              background: '#fff', borderRadius: 0, padding: '20px',
              border: '1px solid #e2e8f0', marginBottom: '32px', textAlign: 'left',
            }}>
              <p style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '12px' }}>
                What happens next
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['Your full programme is now unlocked', 'Brian remembers your assessment and will guide you', 'A confirmation email is on its way', 'Start Stage 2 whenever you are ready'].map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
                    <span style={{ color: '#16a34a', fontWeight: 700, flexShrink: 0 }}>→</span>
                    {step}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => navigate('/portal')}
              data-testid="payment-go-to-portal-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: '#1a1a1a', color: '#F6ADBC', border: 'none',
                padding: '16px 36px', borderRadius: 0,
                fontSize: '16px', fontWeight: 700, cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
              onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
            >
              Go to Your Springboard
              <ArrowRight style={{ width: 18, height: 18 }} />
            </button>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="payment-failed">
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%',
              background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <XCircle style={{ width: 36, height: 36, color: '#ef4444' }} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#1E491A', marginBottom: '12px' }}>
              Payment Not Confirmed
            </h1>
            <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px' }}>
              We could not confirm your payment. No charge has been made. You can try again from the pricing page.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              data-testid="payment-retry-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: '#1a1a1a', color: '#F6ADBC', border: 'none',
                padding: '14px 32px', borderRadius: 0,
                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back to Pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
